import React from "react";
import { Box, Typography } from "@mui/material";
import firmaDirector from "../../../assets/firma-director.jpg";
import logoBremar from "../../../assets/logo-bremar.png";

const BLUE = "#1975d1";
const INK = "#1f2937";
const MUTED = "#6b7280";

function Fila({ etiqueta, valor }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "160px 1fr" },
        gap: { xs: 0.25, sm: 2 },
        py: 1.15,
        borderBottom: "1px solid #e8eef5",
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: BLUE,
          pt: { sm: 0.15 },
        }}
      >
        {etiqueta}
      </Typography>
      <Typography sx={{ fontSize: 14, color: INK, lineHeight: 1.45, fontWeight: 500 }}>
        {valor}
      </Typography>
    </Box>
  );
}

export default function CertificateDocument({ certificado, qrSvg }) {
  if (!certificado) return null;

  const { folio, empresa, tecnico, normas } = certificado;
  const licencia = [empresa.licencia, empresa.licenciaVigencia]
    .filter((v) => v && v !== "—")
    .join(" · ");

  return (
    <Box
      className="certificado-print-area"
      sx={{
        width: "100%",
        maxWidth: "8.5in",
        minHeight: { xs: "auto", sm: "11in" },
        mx: "auto",
        bgcolor: "#fff",
        color: INK,
        display: "flex",
        flexDirection: "column",
        px: { xs: 2.5, sm: "0.7in" },
        py: { xs: 2.5, sm: "0.55in" },
        boxShadow: { xs: "0 8px 28px rgba(15, 23, 42, 0.08)", sm: "0 12px 40px rgba(15, 23, 42, 0.1)" },
        borderLeft: { xs: `5px solid ${BLUE}`, sm: `8px solid ${BLUE}` },
        printColorAdjust: "exact",
        WebkitPrintColorAdjust: "exact",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          pb: 2,
        }}
      >
        <Box
          component="img"
          src={logoBremar}
          alt="BreMar control de plagas S.A. de C.V."
          sx={{
            height: { xs: 56, sm: 78 },
            width: "auto",
            maxWidth: { xs: 160, sm: 210 },
            objectFit: "contain",
          }}
        />
        <Box sx={{ textAlign: "right", minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: { xs: 11, sm: 12 },
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: BLUE,
              lineHeight: 1.35,
            }}
          >
            Certificado de
            <Box component="span" sx={{ display: "block" }}>
              control de plagas
            </Box>
          </Typography>
          <Typography sx={{ mt: 0.75, fontSize: 12, fontWeight: 700, color: INK, wordBreak: "break-all" }}>
            {folio}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ height: "2px", bgcolor: BLUE, mb: 2.5 }} />

      <Typography sx={{ fontSize: { xs: 13, sm: 14 }, color: INK, lineHeight: 1.65 }}>
        <Box component="span" sx={{ fontWeight: 700 }}>
          BreMar control de plagas, S.A. de C.V.
        </Box>{" "}
        certifica que se realizó el siguiente servicio de control de plagas:
      </Typography>

      <Box sx={{ mt: 2.5 }}>
        <Fila etiqueta="Cliente" valor={certificado.cliente} />
        <Fila etiqueta="Inmueble" valor={certificado.lugar} />
        <Fila etiqueta="Servicio" valor={certificado.servicio} />
        <Fila etiqueta="Periodo" valor={certificado.mes} />
        <Fila etiqueta="Fecha" valor={certificado.fecha} />
        <Fila etiqueta="Técnico" valor={`${tecnico.nombre} · ${tecnico.puesto}`} />
      </Box>

      {(normas || []).length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: BLUE,
              mb: 0.75,
            }}
          >
            Normatividad
          </Typography>
          {(normas || []).map((norma) => (
            <Typography key={norma} sx={{ fontSize: 12.5, color: MUTED, lineHeight: 1.55, mb: 0.25 }}>
              {norma}
            </Typography>
          ))}
        </Box>
      )}

      <Box sx={{ flex: 1, minHeight: { xs: 24, sm: 40 } }} />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr auto" },
          gap: { xs: 3, sm: 4 },
          alignItems: "end",
        }}
      >
        <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
          <Box
            component="img"
            src={firmaDirector}
            alt="Firma de Raúl Marín Huerta, Director General"
            sx={{
              display: "block",
              maxWidth: { xs: 190, sm: 220 },
              width: "100%",
              height: "auto",
              mixBlendMode: "multiply",
              mx: { xs: "auto", sm: 0 },
            }}
          />
        </Box>

        {qrSvg ? (
          <Box sx={{ textAlign: "center", justifySelf: { sm: "end" } }}>
            <Box
              role="img"
              aria-label={`Código QR para verificar el certificado ${folio}`}
              sx={{
                mx: "auto",
                width: 92,
                height: 92,
                "& svg": { width: "100%", height: "100%", display: "block" },
              }}
              dangerouslySetInnerHTML={{
                __html: qrSvg.replace("<svg", '<svg width="100%" height="100%"'),
              }}
            />
            <Typography sx={{ mt: 0.75, fontSize: 11, fontWeight: 600, color: BLUE }}>
              Verificación
            </Typography>
          </Box>
        ) : null}
      </Box>

      {licencia ? (
        <Typography sx={{ mt: 2.5, fontSize: 12, color: MUTED }}>
          Licencia o registro: {licencia}
        </Typography>
      ) : null}

      <Box sx={{ mt: 2, pt: 1.75, borderTop: `1px solid ${BLUE}` }}>
        <Typography sx={{ fontSize: 11.5, color: MUTED, lineHeight: 1.55 }}>
          Este certificado solo cubre el servicio, el inmueble y la fecha de arriba.
        </Typography>
      </Box>
    </Box>
  );
}
